export const CHUNK_RELOAD_KEY = 'chunk-reload-attempted'

/** Injected by client/tools/deploy.ts; empty in local dev and in tests */
const RUNNING_BUILD_ID = process.env.BUILD_ID || 'unknown'

const isChunkLoadError = (error: Error) =>
  error.name === 'ChunkLoadError' || /Loading (CSS )?chunk .* failed/i.test(error.message)

/**
 * A lazy route chunk that fails to load leaves a dead screen. Reloading picks
 * up the current index.html and its build folder. Guarded by sessionStorage so
 * a build that is genuinely broken shows the error instead of looping.
 */
export const handleChunkLoadError = (error: Error) => {
  if (!isChunkLoadError(error)) return false

  try {
    // Only a guard from this same build blocks the reload: a guard left by an
    // older build means the previous reload landed on a different build whose
    // first chunk failed before App's boot effect could clear it
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === RUNNING_BUILD_ID) return false
    sessionStorage.setItem(CHUNK_RELOAD_KEY, RUNNING_BUILD_ID)
  } catch {
    // Private-mode browsers throw on sessionStorage; without a guard we cannot
    // promise a single reload, so leave the error to the ErrorBoundary
    return false
  }

  window.location.reload()
  return true
}

/**
 * Called once the app shell has mounted, which is the unambiguous sign that a
 * boot succeeded. Clearing the guard then makes the promise "one reload per
 * broken build" instead of "one per tab session" — a tab left open across two
 * deploys would otherwise be stuck on a dead screen the second time.
 *
 * Only cleared when the recorded build id differs from the running one: the
 * reload landed on a genuinely different build, so recovery worked. Keeping the
 * guard for the same build id is what stops a build whose chunks are actually
 * missing from reload-looping after each boot.
 */
export const clearChunkReloadGuardOnBoot = () => {
  try {
    const attemptedBuildId = sessionStorage.getItem(CHUNK_RELOAD_KEY)
    if (attemptedBuildId && attemptedBuildId !== RUNNING_BUILD_ID) sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    // Private-mode browsers throw on sessionStorage; nothing to clear then
  }
}
